import React, { useEffect, useState } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import toast, { Toaster } from 'react-hot-toast';

function ConsultationAdd() {
  const { id } = useParams();
  const navigate = useNavigate();
  const BASE_URL = process.env.REACT_APP_BASEURL;
  const getAuthToken = () => localStorage.getItem("astroVani-admin-token");
  const [loading, setLoading] = useState(false);

  const categories = [
    { value: "First Recharge", label: "First Recharge" },
    { value: "Second Recharge", label: "Second Recharge" },
    { value: "Third Recharge", label: "Third Recharge" },
    { value: "Fourth Recharge", label: "Fourth Recharge" },
    { value: "Fifth Recharge", label: "Fifth recharge and onwards" }
  ];

  const { register, control, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      category: '',
      fields: [{ 
        bonusType: 'percentage', 
        bonusValue: '', 
        minimumRechargeAmount: '', 
        startDate: '', 
        endDate: '', 
        startTime: '', 
        endTime: '', 
        status: 'active' 
      }]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'fields'
  });

  // Fetch existing data if editing
  useEffect(() => {
    const fetchData = async () => {
      if (id && id !== 'undefined' && id !== 'null') {
        try {
          const response = await fetch(`${BASE_URL}/admin/rechargeCampaign/${id}`, {
            headers: {
              'Authorization': getAuthToken(),
            },
          });

          if (!response.ok) throw new Error("Failed to fetch campaign details");
          const data = await response.json();

          if (data?.data) {
            const item = data.data;
            reset({
              category: item.category || '',
              fields: [{
                bonusType: item.bonusType || 'percentage',
                bonusValue: item.bonusValue || '',
                minimumRechargeAmount: item.minimumRechargeAmount || '',
                startDate: item.startDate ? item.startDate.split('T')[0] : '',
                endDate: item.endDate ? item.endDate.split('T')[0] : '',
                startTime: item.startTime || '',
                endTime: item.endTime || '',
                status: item.status || 'active',
              }]
            });
          }
        } catch (error) {
          console.error("Fetch error:", error);
          toast.error(error.message || "Failed to fetch details");
        }
      }
    };

    fetchData();
  }, [id, reset, BASE_URL]);

  const onSubmit = async (data) => {
    const { category, fields } = data;
    setLoading(true);
    try {
      const sanitizedFields = fields.map(item => ({
        ...item,
        category,
        bonusValue: Number(item.bonusValue),
        minimumRechargeAmount: Number(item.minimumRechargeAmount),
        status: id ? item.status : "active",
      }));

      const url = id 
        ? `${BASE_URL}/admin/rechargeCampaign/${id}` 
        : `${BASE_URL}/admin/rechargeCampaign`;
      
      const method = id ? 'PUT' : 'POST';
      const bodyPayload = id ? sanitizedFields[0] : sanitizedFields;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': getAuthToken(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bodyPayload)
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || "Failed to save campaign");
      }

      toast.success(id ? "Campaign updated successfully!" : "Campaigns added successfully!");
      navigate('/campaign');
    } catch (err) {
      toast.error(err?.message || "Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div style={{ marginBottom: "20px", marginTop: "10px", display: "flex", alignItems: "center" }}>
        <button
          onClick={() => window.history.back()}
          style={{
            backgroundColor: "transparent",
            border: "none",
            cursor: "pointer",
            fontFamily: "monospace",
            fontSize: "18px",
            color: "#007bff",
            display: "flex",
            alignItems: "center",
            position: "relative",
            padding: "10px 20px",
          }}
        >
          <span style={{ marginRight: "8px" }}>&lt;</span>
          <span style={{ position: "relative", display: "inline-block" }}>
            Back
            <span
              style={{
                position: "absolute",
                left: 0,
                bottom: -2,
                width: "100%",
                height: "1px",
                borderBottom: "2px solid #007bff",
              }}
            ></span>
          </span>
        </button>
        <h5 style={{ margin: 0, fontWeight: 600, color: "#333" }}>
          {id ? "Edit Campaign" : "Add Campaign"}
        </h5>
      </div>

      <Toaster position="top-right" reverseOrder={true} />
      <div className="card-body">
        <form className="row g-4" onSubmit={handleSubmit(onSubmit)}>
          {/* Category Dropdown */}
          <div className="col-md-6">
            <label className="form-label">Category</label> <span style={{ color: "red" }}>*</span>
            <Controller
              name="category"
              control={control}
              rules={{ required: "Category is required" }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={categories}
                  placeholder="Select Category"
                  value={categories.find(option => option.value === field.value)}
                  onChange={(selectedOption) => field.onChange(selectedOption.value)}
                />
              )}
            />
            {errors.category && <span className="text-danger" style={{ fontSize: '12px' }}>{errors.category.message}</span>}
          </div>

          {/* Dynamic Fields */}
          {fields.map((item, index) => (
            <div key={item.id} className="col-12 border p-3 rounded position-relative">
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label">Bonus Type</label>
                  <select className="form-control" {...register(`fields.${index}.bonusType`, { required: true })}>
                    <option value="percentage">Percentage</option>
                    <option value="amount">Amount</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Bonus Value</label>
                  <input className="form-control" type="number" min="0" step="any"
                    {...register(`fields.${index}.bonusValue`, { required: true })} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Min Recharge Amount</label>
                  <input className="form-control" type="number" min="0" step="any"
                    {...register(`fields.${index}.minimumRechargeAmount`, { required: true })} />
                </div>
                
                <div className="col-md-3">
                  <label className="form-label">Start Date</label>
                  <input className="form-control" type="date"
                    {...register(`fields.${index}.startDate`, { required: true })} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">End Date</label>
                  <input className="form-control" type="date"
                    {...register(`fields.${index}.endDate`, { required: true })} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Start Time</label>
                  <input className="form-control" type="time"
                    {...register(`fields.${index}.startTime`, { required: true })} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">End Time</label>
                  <input className="form-control" type="time"
                    {...register(`fields.${index}.endTime`, { required: true })} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Status</label>
                  <select className="form-control" disabled={!id} {...register(`fields.${index}.status`)}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                {!id && (
                  <div className="col-md-12 d-flex justify-content-end align-items-end mt-3">
                    {index > 0 && (
                      <button type="button" className="btn btn-danger mx-2" onClick={() => remove(index)}> - Remove </button>
                    )}
                    {index === fields.length - 1 && (
                      <button type="button" className="btn btn-success" onClick={() => append({ 
                        bonusType: 'percentage', bonusValue: '', minimumRechargeAmount: '', 
                        startDate: '', endDate: '', startTime: '', endTime: '', status: 'active' 
                      })}> + Add More </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Form Actions */}
          <div className="col-12 mt-4">
            <button type="submit" className="btn btn-primary px-3" disabled={loading}>
              {loading ? 'Saving...' : id ? 'Update' : 'Save'}
            </button>
            <Link to="/campaign" className="btn btn-secondary px-3 mx-2">Cancel</Link>
          </div>
        </form>
      </div>
    </>
  );
}

export default ConsultationAdd;
